"use client";

import { useEffect, useState } from "react";
import { getActiveSignupLimitRulesPublic, listTimeSlotsForEventPublic } from "../_logic/actions/public-actions";
import type { ActivityTimeSlotRow } from "../_logic/types/db-types";
import { EMPTY_SIGNUP_LIMIT_RULES, type SignupLimitRules } from "../_logic/utils/activity-signup-status";
import { groupTimeSlotsByActivity } from "../_logic/utils/time-slot-utils";

interface UseSignupLimitRulesResult {
  rules: SignupLimitRules;
  /** Event-wide time slots grouped by activity id — a day cap counts registrations across every
   * activity on a date, so evaluating a block needs slots beyond whichever one activity a caller
   * is looking at. Only fetched once `rules` comes back with something configured — nothing needs
   * this map when the event has no day caps or exclusion groups. */
  timeSlotsByActivity: Map<number, ActivityTimeSlotRow[]>;
}

/**
 * Loads the event's day-cap/exclusion-group signup-limit rules on demand, plus the event-wide
 * slot map those rules need to evaluate against. For a surface (like a single-activity modal)
 * that doesn't already have the event-wide slot map fetched for other reasons — one that does
 * (e.g. ActivitiesRenderer's page-load fetch) should keep using that map directly rather than
 * pay for a second copy of the same data through this hook.
 *
 * @param eventId - The event ID.
 * @param enabled - Skip fetching entirely, e.g. while the caller has nothing to evaluate yet.
 */
export function useSignupLimitRules(eventId: number, enabled: boolean): UseSignupLimitRulesResult {
  const [rules, setRules] = useState<SignupLimitRules>(EMPTY_SIGNUP_LIMIT_RULES);
  const [timeSlotsByActivity, setTimeSlotsByActivity] = useState<Map<number, ActivityTimeSlotRow[]>>(new Map());

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    getActiveSignupLimitRulesPublic(eventId)
      .then(async (fetchedRules) => {
        if (cancelled) return;
        setRules(fetchedRules);
        if (fetchedRules.dayCapsByDate.size === 0 && fetchedRules.exclusionGroups.length === 0) return;

        const allSlots = await listTimeSlotsForEventPublic(eventId);
        if (cancelled) return;
        setTimeSlotsByActivity(groupTimeSlotsByActivity(allSlots));
      })
      .catch((error) => {
        console.error("useSignupLimitRules: failed to load signup-limit rule data", { eventId }, error);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, eventId]);

  return { rules, timeSlotsByActivity };
}
